import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Slot as SlotPrimitive } from 'radix-ui';
import { tv } from 'tailwind-variants';
import { label } from '../typography/Label';
import { LoadingDots } from './LoadingDots';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import type { VariantProps } from 'tailwind-variants';

// Solid-fill variants (primary/destructive/positive) use a dotted inset outline
// instead of a solid inset ring: their text-color ring blends into the page at
// the button's edge (primary in both modes, destructive/positive on the light
// page), so a solid ring reads as a radius shrink rather than a focus ring. The
// dotted pattern reads regardless; 2px so it's obvious under keyboard nav.
const dottedFocusOutline = `
  focus-visible:ring-0
  focus-visible:outline-2
  focus-visible:outline-dotted
  focus-visible:outline-current
  focus-visible:-outline-offset-2
`;
const button = tv({
  extend: label,
  base: `
    relative
    focus-visible:ring-current
    aria-invalid:ring-destructive/20
    dark:aria-invalid:ring-destructive/40
    aria-invalid:border-destructive
    disabled:text-disabled-foreground
    inline-flex
    shrink-0
    items-center
    justify-center
    rounded-sm
    text-sm
    whitespace-nowrap
    transition-all
    outline-none
    focus-visible:ring-1
    focus-visible:ring-inset
    enabled:cursor-pointer
    disabled:pointer-events-none
    [&_svg]:pointer-events-none
    [&_svg]:shrink-0
  `,
  variants: {
    variant: {
      primary: `
        bg-primary
        text-primary-foreground
        hover:bg-primary-hover
        shadow-xs
        ${dottedFocusOutline}
      `,
      secondary: `
        bg-background
        border-border-secondary
        text-primary
        hover:bg-background-secondary
        border
        shadow-xs
      `,
      tertiary: `
        text-primary
        bg-background-secondary
        hover:bg-background-tertiary
      `,
      quaternary: `
        text-primary
        hover:text-content-secondary
        underline
        px-0!
      `,
      destructive: `
        bg-background-negative-solid
        text-white
        hover:brightness-110
        disabled:bg-disabled-background
        disabled:text-disabled-foreground
        shadow-xs
        ${dottedFocusOutline}
      `,
      positive: `
        bg-background-positive-solid
        text-white
        hover:brightness-110
        disabled:bg-disabled-background
        disabled:text-disabled-foreground
        shadow-xs
        ${dottedFocusOutline}
      `
    },
    size: {
      sm: `
        h-6
        gap-1
        px-2
        has-[>svg]:px-2.5
      `,
      md: `
        h-8
        gap-1
        px-3
        has-[>svg]:px-2.5
      `,
      lg: `
        h-10
        gap-1.5
        px-5
        py-2
        has-[>svg]:px-3.5
        [&_svg:not([class*=_size-_])]:size-4!
      `,
      icon: `size-9`
    }
  },
  compoundVariants: [{
    variant: ['primary', 'secondary', 'tertiary', 'destructive', 'positive'],
    className: 'disabled:bg-disabled-background'
  }, {
    size: ['sm', 'md'],
    className: '[&_svg:not([class*=_size-_])]:size-3!'
  }],
  defaultVariants: {
    variant: 'primary',
    size: 'lg',
    weight: 'medium'
  }
});

// Helper functions for mapping props
const mapButtonSizeToLoadingDotsSize = (buttonSize: NonNullable<VariantProps<typeof button>['size']>): NonNullable<React.ComponentProps<typeof LoadingDots>['size']> => {
  if (buttonSize === 'sm') return 'sm';
  if (buttonSize === 'md') return 'sm';
  if (buttonSize === 'lg') return 'md';
  if (buttonSize === 'icon') return 'sm';
  // Default or fallback, though ideally all cases are handled
  return 'md';
};
const mapButtonVariantToLoadingDotsVariant = (buttonVariant: NonNullable<VariantProps<typeof button>['variant']>): NonNullable<React.ComponentProps<typeof LoadingDots>['variant']> => {
  if (buttonVariant === 'primary' || buttonVariant === 'destructive' || buttonVariant === 'positive') return 'light';
  if (['secondary', 'tertiary', 'quaternary'].includes(buttonVariant)) return 'dark';
  // Default or fallback
  return 'dark';
};

// Helper function to wrap content with loading state
const withLoadingState = (content: React.ReactNode, pending: boolean, loadingDotsSize: NonNullable<React.ComponentProps<typeof LoadingDots>['size']>, loadingDotsVariant: NonNullable<React.ComponentProps<typeof LoadingDots>['variant']>): React.ReactNode => <>
    {pending ? <>
        <span className="opacity-0">{content}</span>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <LoadingDots size={loadingDotsSize} variant={loadingDotsVariant} />
        </div>
      </> : content}
  </>;
type Props = {
  asChild?: boolean;
  pending?: boolean;
  tooltip?: string;
};
type ButtonProps = React.ComponentProps<'button'> & VariantProps<typeof button> & Props;
function Button(t0) {
  const $ = _c(40);
  let children;
  let className;
  let props;
  let sizeProp;
  let t1;
  let t2;
  let tooltip;
  let variantProp;
  if ($[0] !== t0) {
    ({
      className,
      variant: variantProp,
      size: sizeProp,
      asChild: t1,
      pending: t2,
      tooltip,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = sizeProp;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltip;
    $[8] = variantProp;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    sizeProp = $[4];
    t1 = $[5];
    t2 = $[6];
    tooltip = $[7];
    variantProp = $[8];
  }
  const asChild = t1 === undefined ? false : t1;
  const pending = t2 === undefined ? false : t2;
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  const resolvedVariant = variantProp ?? button.defaultVariants.variant;
  const resolvedSize = sizeProp ?? button.defaultVariants.size;
  let t3;
  if ($[9] !== Comp || $[10] !== asChild || $[11] !== children || $[12] !== className || $[13] !== pending || $[14] !== props || $[15] !== resolvedSize || $[16] !== resolvedVariant || $[17] !== sizeProp || $[18] !== variantProp) {
    const loadingDotsSize = mapButtonSizeToLoadingDotsSize(resolvedSize);
    const loadingDotsVariant = mapButtonVariantToLoadingDotsVariant(resolvedVariant);
    let t4;
    if ($[20] !== className || $[21] !== sizeProp || $[22] !== variantProp) {
      t4 = button({
        variant: variantProp,
        size: sizeProp,
        className
      });
      $[20] = className;
      $[21] = sizeProp;
      $[22] = variantProp;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    const t5 = props.disabled || pending;
    const t6 = pending ? "true" as const : undefined;
    const t7 = pending ? "polite" as const : undefined;
    let t8;
    if ($[24] !== props || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
      t8 = {
        "data-slot": "button",
        className: t4,
        disabled: t5,
        "aria-busy": t6,
        "aria-live": t7,
        ...props
      };
      $[24] = props;
      $[25] = t4;
      $[26] = t5;
      $[27] = t6;
      $[28] = t7;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    const commonButtonProps = t8;
    const renderAsChildContent = () => {
      if (!React.isValidElement(children)) {
        return withLoadingState(children, pending, loadingDotsSize, loadingDotsVariant);
      }
      const elementWithChildren = children as React.ReactElement<{
        children?: React.ReactNode;
      }>;
      const originalElementChildren = elementWithChildren.props.children;
      return React.cloneElement(elementWithChildren, {}, withLoadingState(originalElementChildren, pending, loadingDotsSize, loadingDotsVariant));
    };
    t3 = asChild ? <Comp {...commonButtonProps}>{renderAsChildContent()}</Comp> : <Comp {...commonButtonProps}>{withLoadingState(children, pending, loadingDotsSize, loadingDotsVariant)}</Comp>;
    $[9] = Comp;
    $[10] = asChild;
    $[11] = children;
    $[12] = className;
    $[13] = pending;
    $[14] = props;
    $[15] = resolvedSize;
    $[16] = resolvedVariant;
    $[17] = sizeProp;
    $[18] = variantProp;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const buttonElement = t3;
  if (tooltip) {
    const isDisabled = props.disabled || pending;
    let t4;
    if ($[30] !== buttonElement || $[31] !== isDisabled) {
      t4 = isDisabled ? <span className="inline-flex cursor-not-allowed" tabIndex={0}>{buttonElement}</span> : buttonElement;
      $[30] = buttonElement;
      $[31] = isDisabled;
      $[32] = t4;
    } else {
      t4 = $[32];
    }
    const triggerElement = t4;
    let t5;
    if ($[33] !== triggerElement) {
      t5 = <TooltipTrigger asChild={true}>{triggerElement}</TooltipTrigger>;
      $[33] = triggerElement;
      $[34] = t5;
    } else {
      t5 = $[34];
    }
    let t6;
    if ($[35] !== tooltip) {
      t6 = <TooltipContent>{tooltip}</TooltipContent>;
      $[35] = tooltip;
      $[36] = t6;
    } else {
      t6 = $[36];
    }
    let t7;
    if ($[37] !== t5 || $[38] !== t6) {
      t7 = <Tooltip>{t5}{t6}</Tooltip>;
      $[37] = t5;
      $[38] = t6;
      $[39] = t7;
    } else {
      t7 = $[39];
    }
    return t7;
  }
  return buttonElement;
}
export { button, Button, type ButtonProps };